import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { styled } from '@abyss/web/tools/styled';
import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Markdown } from '@abyss/web/ui/Markdown';
import { Modal } from '@abyss/web/ui/Modal';
import { Text } from '@abyss/web/ui/Text';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeModalTrackEvent } from '../../../../common/AdobeTagging/adobeModalTrackEvent';
import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../../common/Utils';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../../hooks/useNotAvailableUnknownText';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import { phoneOnly } from '../../../ConstantsStyles';
import { CulturalCompetencyTraining } from '../../../modal/CulturalCompetencyTraining';

export const ModalContent = styled(Markdown, {
  fontSize: '14px',
  lineHeight: '18px',
  color: '#323334',
});

export const ModalContainer = styled(Modal, {
  '.abyss-modal-content-container': {
    width: 'auto',
    height: 'auto',
    maxWidth: '572px',
    maxHeight: '900px',
  },
  '.abyss-modal-content-title': {
    lineHeight: '40px',
    fontSize: '32.43px',
    fontWeight: '600',
    '@screen < $sm': {
      lineHeight: '36px',
      fontSize: '28.83px',
      fontWeight: '600',
    },
  },
});

export const ListItem = styled('ul', {
  listStyle: 'disc',
  marginLeft: '28px',
  fontSize: '18px',
  color: '#4B4D4F',
  marginTop: '12px',
  marginBottom: '24px',
  fontWeight: '$normal',
  '@screen < $sm': { fontSize: '16px' },
  '& li > span': { display: 'flex', gap: '8px', marginBottom: '10px' },
  '& li::marker': { fontSize: '14px' },
  '& li b': { fontWeight: 'normal' },
});

type Competence = {
  category?: string;
  description?: string;
};

type Props = {
  culturalCompetencies?: Competence[] | string[];
  mobileScreen: boolean;
  isProviderExperienceSectionEnabled?: boolean;
};
export const ListCulturalCompetencies = ({
  culturalCompetencies = [],
  mobileScreen,
  isProviderExperienceSectionEnabled,
}: Props) => {
  const { t } = useTranslation();
  const phoneScreen = useMediaQuery(phoneOnly);
  const featureFlags = useLagoon('feature-flags')();
  const isEnableL2CulturalCompetencies = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_L2_CULTURAL_COMPETENCIES
  );
  const enableCulturalCompetency = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CULTURAL_COMPETENCY_TRAINING_SECTION
  );

  const [isOpen, setIsOpen] = useState(false);
  const culturalCompetencyModalRef = useRef(null);
  const { providerDetails: notAvailableOrUnknownText } =
    useNotAvailableUnknownText();

  const isGEHAV2 = shouldUseGehaV2Styling();
  const additonalCss = isGEHAV2 ? '#00172F' : '#4B4D4F';

  return (
    <React.Fragment>
      {enableCulturalCompetency && !isOpen ? (
        <Flex
          css={{
            width: 'max-content',
          }}
          direction="row"
        >
          <b data-testid="cultural-competency-heading">
            {t(
              'PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.CULTURAL_COMPETENCY_TRAINING'
            )}
          </b>

          <Button
            aria-label="Information about cultural competency training"
            css={{
              'abyss-button-root': {
                display: 'inline-block',
                verticalAlign: 'middle',
                padding: '0px',
                height: 'fit-content',
                marginLeft: '4px',
              },
            }}
            data-testid="cultural-competency-tooltip-button"
            onClick={() => {
              setIsOpen(true);
              setTimeout(() => {
                if (culturalCompetencyModalRef?.current) {
                  culturalCompetencyModalRef.current?.focus();
                }
              }, 0);
              adobeModalTrackEvent(
                Constants.ADOBE_TRACKING.CULTURAL_COMPETENCY_TRAINING
              );
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                adobeModalTrackEvent(
                  Constants.ADOBE_TRACKING.MODAL_TRACKING_ONLY
                );
              }
            }}
            role="button"
            tabIndex={0}
            variant="outlined"
          >
            <IconMaterial
              css={{
                'abyss-icon-material': {
                  '@screen < $sm': {
                    marginTop: '3px',
                  },
                },
              }}
              icon="info"
              size={phoneScreen ? 18 : 24}
              variant="outlined"
            />
          </Button>
        </Flex>
      ) : (
        <b>
          {t('PROVIDER_DETAILS.ABOUT_TAB.DETAILS_SECTION.CULTURAL_COMPETENCE')}
        </b>
      )}
      {/* Always render the modal, but only open when isOpen is true */}
      {enableCulturalCompetency && (
        <CulturalCompetencyTraining
          isOpen={isOpen}
          ref={culturalCompetencyModalRef}
          setIsOpen={setIsOpen}
        />
      )}
      {isProviderExperienceSectionEnabled ? (
        <>
          {culturalCompetencies?.length > 0 &&
            isEnableL2CulturalCompetencies && (
              <ListItem color="#4B4D4F">
                {culturalCompetencies?.map((competence) => (
                  <li
                    data-testid="provider-experience-section-cultural-competence"
                    key={competence}
                  >{`${competence?.category}: ${competence?.description}`}</li>
                ))}
              </ListItem>
            )}
          {culturalCompetencies?.length > 0 &&
            !isEnableL2CulturalCompetencies && (
              <ListItem color="#4B4D4F">
                {culturalCompetencies?.map((competence) => (
                  <li
                    data-testid="provider-experience-section-cultural-competence"
                    key={competence}
                  >
                    {competence}
                  </li>
                ))}
              </ListItem>
            )}
          {!culturalCompetencies && (
            <ListItem color={additonalCss}>
              <li
                data-testid="provider-experience-section-cultural-competence"
                style={{ color: additonalCss }}
              >
                {notAvailableOrUnknownText}
              </li>
            </ListItem>
          )}
        </>
      ) : (
        <Layout.Stack alignItems="left" space={2}>
          {culturalCompetencies?.length > 0 &&
            isEnableL2CulturalCompetencies &&
            culturalCompetencies?.map(
              (competence) =>
                `${competence?.category}: ${competence?.description}`
            )}
          {culturalCompetencies?.length > 0 &&
            !isEnableL2CulturalCompetencies &&
            culturalCompetencies?.map((competence) => `${competence}`)}
          {(culturalCompetencies == null ||
            culturalCompetencies.length == 0) && (
            <Text
              color="#4B4D4F"
              fontWeight="$medium"
              size={mobileScreen ? '$md' : '18px'}
            >
              {notAvailableOrUnknownText}
            </Text>
          )}
        </Layout.Stack>
      )}
    </React.Fragment>
  );
};
